import { ActionsQueueSyncService } from '@one/app/components/main/services/actions-queue-sync.service';
import { IAccount } from '@one/app/shared/models/account/account.models';
import { AccountService } from '@one/app/shared/services/account/account.service';
import {
  BROWSER_SESSION_FINGERPRINT_STORAGE_KEY,
  buildBrowserSessionFingerprint,
} from '@one/app/shared/services/browser-session/browser-session-fingerprint.util';
import { WINDOW } from '@one/app/shared/tokens/window.token';
import { Injectable, inject } from '@angular/core';
import { QimaOptionalType } from '@qima/ngx-qima';
import { distinctUntilChanged, map } from 'rxjs/operators';

/**
 * Keeps every open QIMAone tab on a single signed-in account: when login, logout, or profile
 * switch happens in one tab, other tabs stop inspector sync and reload to the app root.
 */
@Injectable({
  providedIn: 'root',
})
export class BrowserSessionCoordinatorService {
  private readonly _accountService = inject(AccountService);
  private readonly _actionsQueueSyncService = inject(ActionsQueueSyncService);
  private readonly _window = inject(WINDOW);

  private _isInitialized = false;

  public init(): void {
    if (this._isInitialized) {
      return;
    }

    this._isInitialized = true;
    this._window.addEventListener('storage', this._onStorage);
    this._accountService
      .getAccount$()
      .pipe(
        map((account: QimaOptionalType<IAccount>): string => buildBrowserSessionFingerprint(account)),
        distinctUntilChanged()
      )
      .subscribe((fingerprint: string): void => {
        // Always set the key on logout (value `logged-out`); never remove it — removal is still handled as a remote logout signal.
        this._window.localStorage.setItem(BROWSER_SESSION_FINGERPRINT_STORAGE_KEY, fingerprint);
      });
  }

  private readonly _onStorage = (event: StorageEvent): void => {
    if (event.storageArea !== this._window.localStorage || event.key !== BROWSER_SESSION_FINGERPRINT_STORAGE_KEY) {
      return;
    }

    // Another tab logged in/out/switched profile, or cleared the key.
    void this._reloadForRemoteSessionChange();
  };

  private async _reloadForRemoteSessionChange(): Promise<void> {
    try {
      await this._actionsQueueSyncService.resetForAccountChange(null, false);
    } finally {
      this._window.location.assign('/');
    }
  }
}
